import { useMemo } from 'react';
import { NavLink } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { OverlayScrollbarsComponent } from 'overlayscrollbars-react';
import { get } from '@/api/client';

interface BoardTabsProps {
  /** 현재 활성 게시판 슬러그 (선택) */
  activeSlug?: string;
}

/**
 * 서버에서 내려주는 게시판 메타 — `GET /boards` 응답 row.
 * 일반 유저 요청 시 `is_active=TRUE AND is_visible=TRUE` 필터가 적용된 목록만 반환되므로,
 * 부동산(숨김) / 폐합 4종(비활성)은 여기 포함되지 않음.
 */
interface BoardRow {
  id: number;
  slug: string;
  name: string;
  description: string | null;
  sort_order: number;
  is_active: boolean;
  is_visible: boolean;
}

/**
 * 탭 표시 순서 — 요청된 순서로 고정
 * 인기(별도) → 매매일지 → 주식게시판 → 코인 → 자유게시판 → 나머지(sort_order)
 * 뉴스(news) 는 HIDDEN_SLUGS 로 탭 미노출. 공시(notice) 는 2026-04-29 재가동.
 * 해외주식(global) 게시판은 2026-05-23 폐합 → 우선순위에서 제외.
 */
const PRIORITY_SLUGS = ['diary', 'stock', 'coin', 'free'];

/**
 * 게시판 탭 네비게이션 — 회색 pill 스타일 + 페이지 전체 폭
 * - 부모 nav: container-base 와 동일한 max-w-6xl mx-auto px-4 (단독 배치)
 * - pill 컨테이너: bg-[#e5e5e5] rounded-md p-1, 페이지 전체 폭(w-full)
 * - 선택 탭: bg-white rounded-md font-semibold text-[var(--color-text)] px-3 py-1.5
 * - 미선택 탭: bg-transparent text-gray-500, hover 시 bg-white/70 + text-text (선택과 유사하게 강조)
 * - 가로 스크롤: PC 는 OverlayScrollbars(다크 테마), 모바일은 기본 브라우저 스크롤
 */
export function BoardTabs({ activeSlug }: BoardTabsProps) {
  /*
   * 게시판 목록은 서버 `GET /boards` 로 동적 조회.
   * 기존 하드코딩(constants/BOARDS) 을 사용하면 부동산·폐합 4종이 계속 노출되는 버그가 있었음 (2026-04-21).
   * React Query 캐시(5분) 로 BoardTabs 다중 마운트 시 네트워크 중복 호출 없음.
   */
  const { data } = useQuery({
    queryKey: ['boards', 'visible'],
    queryFn: () => get<BoardRow[]>('/boards'),
    staleTime: 5 * 60_000,
  });
  const boards = data ?? [];

  /* BoardTabs 에서 아예 노출하지 않는 게시판:
     - event: /events 헤더 링크로만 접근
     - notice-ops / suggest: 푸터 링크로만 접근 (관리자도 탭 미노출)
     ※ news / market 은 일반 BoardPage 가 아닌 전용 페이지(/news, /market)로 라우팅 — 아래 SPECIAL_TARGETS 사용
     ※ 공시(notice) 는 2026-04-29 DART 공시 수집 재가동으로 다시 탭 노출. */
  const HIDDEN_SLUGS = new Set(['event', 'notice-ops', 'suggest']);

  /* 뉴스/시황은 게시판 탭에 노출되지만 BoardPage 가 아닌 전용 페이지로 이동 */
  const SPECIAL_TARGETS: Record<string, string> = {
    news: '/news',
    market: '/market',
  };

  const orderedBoards = useMemo<BoardRow[]>(() => {
    const visible = boards.filter((b) => !HIDDEN_SLUGS.has(b.slug));
    const priority = PRIORITY_SLUGS
      .map((slug) => visible.find((b) => b.slug === slug))
      .filter((b): b is BoardRow => !!b);
    const rest = visible.filter((b) => !PRIORITY_SLUGS.includes(b.slug));
    return [...priority, ...rest];
  }, [boards]);

  return (
    <nav className="container-base mb-5 mt-4">
      <div className="flex w-full gap-1 rounded-md bg-[var(--color-tab-bg)] p-1">
        <OverlayScrollbarsComponent
          className="w-full"
          options={{
            scrollbars: { theme: 'os-theme-dark', autoHide: 'leave', autoHideDelay: 400 },
            overflow: { y: 'hidden' },
          }}
          defer
        >
          <div className="flex gap-1">
            <NavLink
              to="/board/popular"
              className={({ isActive }) => {
                const active = isActive || activeSlug === '__popular__';
                return `shrink-0 cursor-pointer whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-all duration-150 ${
                  active
                    ? 'bg-[var(--color-tab-active)] font-semibold text-[var(--color-text)]'
                    : 'bg-transparent text-[var(--color-text-sub)] hover:bg-[var(--color-tab-hover)] hover:text-[var(--color-text)]'
                }`;
              }}
            >
              인기
            </NavLink>
            {orderedBoards.map((b) => {
              const isActive = activeSlug === b.slug;
              const target = SPECIAL_TARGETS[b.slug] ?? `/board/${b.slug}`;
              return (
                <NavLink
                  key={b.slug}
                  to={target}
                  className={`shrink-0 cursor-pointer whitespace-nowrap rounded-md px-3 py-1.5 text-sm transition-all duration-150 ${
                    isActive
                      ? 'bg-[var(--color-tab-active)] font-semibold text-[var(--color-text)]'
                      : 'bg-transparent text-[var(--color-text-sub)] hover:bg-[var(--color-tab-hover)] hover:text-[var(--color-text)]'
                  }`}
                >
                  {b.name}
                </NavLink>
              );
            })}
          </div>
        </OverlayScrollbarsComponent>
      </div>
    </nav>
  );
}
